import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import {
  useAddRepo,
  useCommitDetail,
  useConnectSource,
  useOrgStatus,
  useRecentCommits,
  useStartInstall,
  type ConnectSourceResult,
  type Installation,
  type OrgStatus,
  type Repo,
} from '../api.ts';
import { Select } from '../components/Select.tsx';

import type { SettingsOutletContext } from './Settings.tsx';

/**
 * Integrations section of /settings/data-sources: GitHub installations
 * + repos + recent-commit browser (was the post-sign-in home in earlier
 * iterations). The commit browser is mostly a debugging aid for
 * verifying that the GitHub App can read the repo's history.
 */
export function SettingsIntegrations() {
  const { orgSlug } = useOutletContext<SettingsOutletContext>();
  return (
    <>
      <h2>Integrations</h2>
      <p className="muted">
        Connect a GitHub installation and pick a repo so we can ingest its
        history. The commit list below confirms the install can read the repo.
      </p>
      <OrgStatusPanel orgSlug={orgSlug} />
      <ConnectSourcePanel orgSlug={orgSlug} />
    </>
  );
}

/**
 * Connect a GitLab or Azure Repos repository with an access token.
 * GitHub has its own App-install flow above; these providers have no
 * App analog, so we store a pasted token (encrypted) and clone with it.
 */
function ConnectSourcePanel({ orgSlug }: { orgSlug: string }) {
  const [repoUrl, setRepoUrl] = useState('');
  const [token, setToken] = useState('');
  const [defaultBranch, setDefaultBranch] = useState('');
  const [result, setResult] = useState<ConnectSourceResult | null>(null);
  const connect = useConnectSource(orgSlug);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!repoUrl || !token) return;
    connect.mutate(
      {
        repo_url: repoUrl,
        token,
        default_branch: defaultBranch.trim() || undefined,
      },
      {
        onSuccess: (r) => {
          setResult(r);
          setToken('');
        },
      },
    );
  };

  return (
    <form onSubmit={submit} className="stack" style={{ marginTop: '1.5rem' }}>
      <h3>Connect GitLab or Azure Repos</h3>
      <p className="muted">
        Paste a repository URL and an access token. The token is stored encrypted and used
        only to clone the repo for history ingest.
      </p>
      <p className="muted">
        <strong>GitLab</strong>: a project, group, or personal access token with the{' '}
        <code>read_repository</code> scope and at least the <strong>Reporter</strong> role
        (Guest can&apos;t clone private code).{' '}
        <strong>Azure DevOps</strong>: a PAT with <strong>Code (Read)</strong> scope.
      </p>
      <label className="row">
        Repository URL
        <input
          type="text"
          placeholder="https://gitlab.com/group/project"
          value={repoUrl}
          onChange={(e) => setRepoUrl(e.target.value)}
          required
        />
      </label>
      <label className="row">
        Access token
        <input
          type="password"
          placeholder="glpat-… or Azure PAT"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          autoComplete="off"
          required
        />
      </label>
      <label className="row">
        Default branch <span className="muted">(optional)</span>
        <input
          type="text"
          placeholder="main"
          value={defaultBranch}
          onChange={(e) => setDefaultBranch(e.target.value)}
        />
      </label>
      <p>
        <button type="submit" className="btn" disabled={connect.isPending || !repoUrl || !token}>
          {connect.isPending ? 'Connecting…' : 'Connect repository'}
        </button>
      </p>
      {connect.isError ? (
        <p style={{ color: '#c0392b' }}>{connect.error.message}</p>
      ) : null}
      {result ? (
        <p className="muted">
          Connected <code>{result.repository.prefix}</code> ·{' '}
          {result.backfill_status === 'unavailable'
            ? 'backfill not started (sandbox unavailable in this environment)'
            : `backfill ${result.backfill_status}${
                result.ingest_run_id ? ` (${result.ingest_run_id})` : ''
              }`}
        </p>
      ) : null}
    </form>
  );
}

function OrgStatusPanel({ orgSlug }: { orgSlug: string }) {
  const status = useOrgStatus(orgSlug);

  if (status.isPending) return <p className="muted">Loading…</p>;
  if (status.isError) {
    return <p style={{ color: '#c0392b' }}>Error loading org: {status.error.message}</p>;
  }
  return <OrgStatusContent orgSlug={orgSlug} status={status.data} />;
}

function OrgStatusContent({ orgSlug, status }: { orgSlug: string; status: OrgStatus }) {
  const startInstall = useStartInstall(orgSlug);

  return (
    <>
      <h3>GitHub installations</h3>
      {status.installations.length === 0 ? (
        <p className="muted">No GitHub App installations connected yet.</p>
      ) : (
        <ul className="bare stack">
          {status.installations.map((i) => (
            <li key={i.id}>
              <code>{i.github_account_login}</code>
              {i.suspended_at ? <span className="pill"> suspended</span> : null}
            </li>
          ))}
        </ul>
      )}
      <p>
        <button
          type="button"
          className="btn"
          disabled={startInstall.isPending}
          onClick={() =>
            startInstall.mutate(
              {},
              {
                onSuccess: ({ install_url }) => {
                  window.location.href = install_url;
                },
              },
            )
          }
        >
          {status.installations.length === 0 ? 'Connect GitHub' : 'Add another GitHub install'}
        </button>
      </p>
      {startInstall.isError ? (
        <p style={{ color: '#c0392b' }}>{startInstall.error.message}</p>
      ) : null}

      <h3>Repos</h3>
      {status.repos.length === 0 ? (
        status.installations.length === 0 ? (
          <p className="muted">Connect a GitHub installation first to add a repo.</p>
        ) : (
          <RepoPicker orgSlug={orgSlug} installations={status.installations} />
        )
      ) : (
        <ul className="bare stack">
          {status.repos.map((r) => (
            <li key={r.id}>
              <RepoCard orgSlug={orgSlug} repo={r} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function RepoCard({ orgSlug, repo }: { orgSlug: string; repo: Repo }) {
  return (
    <div className="stack">
      <div>
        <code>{repo.repo_full_name}</code>
        {repo.default_branch ? <span className="muted"> · {repo.default_branch}</span> : null}
      </div>
      <RecentCommits orgSlug={orgSlug} repo={repo} />
    </div>
  );
}

function RecentCommits({ orgSlug, repo }: { orgSlug: string; repo: Repo }) {
  const commits = useRecentCommits(orgSlug, repo.id);
  const [expandedSha, setExpandedSha] = useState<string | null>(null);

  if (commits.isPending) return <p className="muted">Loading recent commits…</p>;
  if (commits.isError) {
    return (
      <p style={{ color: '#c0392b' }}>Couldn&apos;t load commits: {commits.error.message}</p>
    );
  }
  if (commits.data.length === 0) {
    return (
      <p className="muted">
        No commits visible. Check the GitHub App has Contents: Read access.
      </p>
    );
  }

  return (
    <ul
      className="bare stack"
      style={{ borderLeft: '2px solid rgba(127,127,127,0.25)', paddingLeft: '0.75rem' }}
    >
      {commits.data.map((c) => {
        const open = expandedSha === c.sha;
        return (
          <li key={c.sha}>
            <button
              type="button"
              className="commit-row"
              onClick={() => setExpandedSha(open ? null : c.sha)}
              aria-expanded={open}
            >
              <code>{c.short_sha}</code>{' '}
              <span>{c.message_first_line}</span>{' '}
              <span className="muted">
                · {c.author.name ?? c.author.login ?? 'unknown'}
                {c.author.date ? ` · ${formatRelative(c.author.date)}` : null}
              </span>
            </button>
            {open ? <CommitFiles orgSlug={orgSlug} repoId={repo.id} sha={c.sha} /> : null}
          </li>
        );
      })}
    </ul>
  );
}

function CommitFiles({
  orgSlug,
  repoId,
  sha,
}: {
  orgSlug: string;
  repoId: number;
  sha: string;
}) {
  const detail = useCommitDetail(orgSlug, repoId, sha);

  if (detail.isPending) return <p className="muted" style={{ marginLeft: '1rem' }}>Loading…</p>;
  if (detail.isError) {
    return (
      <p style={{ color: '#c0392b', marginLeft: '1rem' }}>{detail.error.message}</p>
    );
  }

  const { stats, files, html_url } = detail.data;

  return (
    <div style={{ marginLeft: '1rem', marginTop: '0.4rem' }} className="stack">
      <p className="muted">
        <span style={{ color: '#2ecc71' }}>+{stats.additions}</span>{' '}
        <span style={{ color: '#e74c3c' }}>−{stats.deletions}</span>{' '}
        across {files.length} file{files.length === 1 ? '' : 's'} ·{' '}
        <a href={html_url} target="_blank" rel="noreferrer">
          view on GitHub
        </a>
      </p>
      <ul className="bare">
        {files.map((f) => (
          <li key={f.filename}>
            <span className="muted" style={{ display: 'inline-block', width: '5rem' }}>
              {f.status}
            </span>
            <code>{f.filename}</code>{' '}
            <span style={{ color: '#2ecc71' }}>+{f.additions}</span>{' '}
            <span style={{ color: '#e74c3c' }}>−{f.deletions}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatRelative(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function RepoPicker({
  orgSlug,
  installations,
}: {
  orgSlug: string;
  installations: Installation[];
}) {
  const [installationId, setInstallationId] = useState<number>(installations[0]?.id ?? 0);
  const [repoFullName, setRepoFullName] = useState('');
  const addRepo = useAddRepo(orgSlug);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!installationId || !repoFullName) return;
    addRepo.mutate(
      { installation_id: installationId, repo_full_name: repoFullName },
      {
        onSuccess: () => {
          setRepoFullName('');
        },
      },
    );
  };

  return (
    <form onSubmit={submit} className="stack">
      <p className="muted">
        Track a repo in this installation as the org&apos;s data source. Use the form{' '}
        <code>owner/repo</code>.
      </p>
      {installations.length > 1 ? (
        <label className="row">
          Installation
          <Select
            value={installationId}
            onChange={(e) => setInstallationId(Number(e.target.value))}
          >
            {installations.map((i) => (
              <option key={i.id} value={i.id}>
                {i.github_account_login}
              </option>
            ))}
          </Select>
        </label>
      ) : null}
      <label className="row">
        Repo
        <input
          type="text"
          placeholder="owner/repo"
          value={repoFullName}
          onChange={(e) => setRepoFullName(e.target.value)}
          required
        />
      </label>
      <p>
        <button type="submit" className="btn" disabled={addRepo.isPending || !repoFullName}>
          Add repo
        </button>
      </p>
      {addRepo.isError ? (
        <p style={{ color: '#c0392b' }}>{addRepo.error.message}</p>
      ) : null}
    </form>
  );
}
